/**
 * Bundle Product Form
 *
 * Manages variant selection across multiple bundle sub-products and submits
 * all selected variants to the cart in a single request.
 * Each product group shows a variant image that updates on option change.
 *
 * Pre-order support: when a variant is unavailable on the main product but
 * available on its pre-order sibling (data-bundle-sibling-variants), the
 * sibling variant is used for cart submission instead.
 */

if (!customElements.get('bundle-product-form')) {
  customElements.define(
    'bundle-product-form',
    class BundleProductForm extends HTMLElement {
      constructor() {
        super();
        this.products = [];
        this.colorPaired = true;    // colors sync across products until unpaired
        this.syncInitiator = null;  // index of the product that "owns" the sync
        this._syncing = false;      // prevents re-entry during programmatic color changes
      }

      connectedCallback() {
        this.submitButton = this.querySelector('[data-bundle-submit]');
        this.submitText = this.querySelector('[data-bundle-submit-text]');
        this.priceText = this.querySelector('[data-bundle-price-text]');
        this.invMsg = this.querySelector('[data-bundle-inv-msg]');
        this.splitNote = this.querySelector('[data-bundle-split-note]');
        this.sectionId = this.dataset.sectionId;

        this.gallery = document.querySelector(`#MediaGallery-${this.sectionId}`);

        this.initProducts();

        // Listen for variant option changes
        this._unsubscribe = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          ({ data }) => {
            const { event } = data;
            const group = event.target.closest('[data-bundle-product]');
            if (!group || !this.contains(group)) return;
            this.handleOptionChange(group, event);
          }
        );

        this.submitButton.addEventListener('click', this.handleSubmit.bind(this));

        this.updatePrice();
        this.updateButtonState();
        this.updateGallery();
      }

      disconnectedCallback() {
        if (this._unsubscribe) this._unsubscribe();
      }

      initProducts() {
        const groups = this.querySelectorAll('[data-bundle-product]');
        this.products = Array.from(groups).map((group) => {
          const variants = JSON.parse(
            group.querySelector('[data-bundle-variants]').textContent
          );

          const siblingEl = group.querySelector('[data-bundle-sibling-variants]');
          const siblingVariants = siblingEl
            ? JSON.parse(siblingEl.textContent)
            : [];

          const selectedVariant = this.resolveVariant(group, variants);

          return {
            element: group,
            variants,
            siblingVariants,
            selectedVariant,
            // The effective variant to add to cart (may be the sibling)
            cartVariant: this.getCartVariant(selectedVariant, group, variants, siblingVariants),
          };
        });
      }

      /**
       * Match checked options against a variants array.
       */
      resolveVariant(group, variants) {
        const variantSelects = group.querySelector('variant-selects');
        if (!variantSelects) return variants[0] || null;

        const checked = Array.from(
          variantSelects.querySelectorAll('fieldset input:checked')
        );
        const selectedValues = checked.map((input) => input.value);

        if (selectedValues.length === 0) return variants[0] || null;

        // Try exact match first (all options selected)
        const exact = this.findMatchingVariant(variants, selectedValues, true);
        if (exact) return exact;

        // Partial match — e.g. color selected but no size yet.
        // Return first variant containing all selected values (for price display).
        return this.findMatchingVariant(variants, selectedValues, false);
      }

      findMatchingVariant(variants, selectedValues, exactOnly) {
        return variants.find((v) => {
          const opts = [v.option1, v.option2, v.option3].filter(
            (o) => o !== null && o !== undefined
          );
          if (exactOnly && selectedValues.length !== opts.length) return false;
          return selectedValues.every((val) => opts.includes(val));
        }) || null;
      }

      /**
       * Determine which variant to actually add to cart.
       * If the main variant is available, use it.
       * If not, try to find a matching available variant on the pre-order sibling.
       */
      getCartVariant(mainVariant, group, variants, siblingVariants) {
        if (!mainVariant) return null;
        if (mainVariant.available) return mainVariant;

        // Main variant unavailable — check sibling
        if (siblingVariants.length === 0) return mainVariant;

        const variantSelects = group.querySelector('variant-selects');
        if (!variantSelects) return mainVariant;

        const checked = Array.from(
          variantSelects.querySelectorAll('fieldset input:checked')
        );
        const selectedValues = checked.map((input) => input.value);

        const sibMatch = this.findMatchingVariant(siblingVariants, selectedValues, true)
          || this.findMatchingVariant(siblingVariants, selectedValues, false);
        if (sibMatch && sibMatch.available) return sibMatch;

        return mainVariant;
      }

      handleOptionChange(group, event) {
        if (this._syncing) return;

        const index = parseInt(group.dataset.bundleProductIndex, 10);
        const product = this.products[index];
        if (!product) return;

        const isColorChange = event && event.target &&
          event.target.closest('.product-form__input--swatch');

        // Color sync logic
        if (isColorChange && this.colorPaired) {
          if (this.syncInitiator === null || this.syncInitiator === index) {
            // This product owns the sync — propagate to others
            this.syncInitiator = index;
            const colorInput = group.querySelector(
              '.product-form__input--swatch input:checked'
            );
            if (colorInput) {
              this.syncColorToOthers(index, colorInput.value);
            }
          } else {
            // A different product changed color → unpair
            this.colorPaired = false;
          }
        }

        this.refreshProduct(index);
        this.updatePrice();
        this.updateButtonState();
        this.updateGallery();
      }

      /** Re-resolve variant and update UI for a single product by index. */
      refreshProduct(index) {
        const product = this.products[index];
        const group = product.element;

        product.selectedVariant = this.resolveVariant(group, product.variants);
        product.cartVariant = this.getCartVariant(
          product.selectedVariant, group, product.variants, product.siblingVariants
        );

        this.updateProductImage(group, product);
        this.updateProductPrice(group, product);
        this.updateProductPreorderState(group, product);
      }

      /* ------------------------------------------------------------------ */
      /*  Color syncing                                                     */
      /* ------------------------------------------------------------------ */

      syncColorToOthers(sourceIndex, colorValue) {
        this._syncing = true;
        this.products.forEach((p, i) => {
          if (i === sourceIndex) return;
          const swatch = p.element.querySelector(
            `.product-form__input--swatch input[value="${CSS.escape(colorValue)}"]`
          );
          if (swatch && !swatch.checked) {
            swatch.checked = true;
            // Fire change event so VariantSelects updates its internal state
            swatch.dispatchEvent(new Event('change', { bubbles: true }));
            this.refreshProduct(i);
          }
        });
        this._syncing = false;
      }

      /* ------------------------------------------------------------------ */
      /*  Gallery filtering by selected color(s)                            */
      /* ------------------------------------------------------------------ */

      getSelectedColors() {
        const colors = new Set();
        this.products.forEach((p) => {
          const input = p.element.querySelector(
            '.product-form__input--swatch input:checked'
          );
          if (input) {
            const handle = input.dataset.colorHandle || input.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
            colors.add(handle);
          }
        });
        return colors;
      }

      updateGallery() {
        if (!this.gallery) return;

        const slides = Array.from(
          this.gallery.querySelectorAll('.product__media-item')
        );
        if (!slides.length) return;

        const selectedColors = this.getSelectedColors();

        // If no colors selected yet, show everything
        if (selectedColors.size === 0) return;

        let firstVisible = null;
        slides.forEach((slide) => {
          const color = slide.dataset.galleryColor;
          const show = !color || color === '__default__' || selectedColors.has(color);
          slide.hidden = !show;
          if (show && !firstVisible) firstVisible = slide;
        });

        // Reset active state and slider
        slides.forEach((s) => s.classList.remove('is-active'));
        if (firstVisible) firstVisible.classList.add('is-active');

        const slider = this.gallery.querySelector('slider-component');
        if (slider && slider.resetPages) slider.resetPages();

        const list = this.gallery.querySelector('.product__media-list');
        if (list) list.scrollTo({ left: 0 });
      }

      /* ------------------------------------------------------------------ */
      /*  Variant image preview                                             */
      /* ------------------------------------------------------------------ */

      updateProductImage(group, product) {
        const img = group.querySelector('[data-bundle-product-image]');
        if (!img) return;

        // Prefer main variant image, fall back to sibling
        const variant = product.selectedVariant || product.cartVariant;
        if (variant && variant.featured_image) {
          img.src = variant.featured_image;
        }
      }

      updateProductPrice(group, product) {
        const v = product.cartVariant || product.selectedVariant;
        if (!v) return;
        group.querySelectorAll('[data-bundle-product-price]').forEach(
          (el) => (el.textContent = this.formatMoney(v.price))
        );
        const hasCompare = v.compare_at_price && v.compare_at_price > v.price;
        group.querySelectorAll('[data-bundle-product-compare-price]').forEach((el) => {
          if (hasCompare) {
            el.textContent = this.formatMoney(v.compare_at_price);
            el.hidden = false;
          } else {
            el.hidden = true;
          }
        });
      }

      productFullySelected(group) {
        const vs = group.querySelector('variant-selects');
        if (!vs) return true;
        const fieldsets = vs.querySelectorAll('fieldset');
        if (fieldsets.length === 0) return true;
        return Array.from(fieldsets).every((fs) => fs.querySelector('input:checked'));
      }

      updateProductPreorderState(group, product) {
        const isPre = this.productFullySelected(group) && this.isPreorder(product);

        group.querySelectorAll('[data-bundle-preorder-badge]').forEach(
          (el) => (el.hidden = !isPre)
        );

        group.querySelectorAll('[data-bundle-ship-date]').forEach((el) => {
          if (isPre && product.cartVariant && product.cartVariant.estimated_ship_date) {
            const date = new Date(product.cartVariant.estimated_ship_date + 'T00:00:00');
            const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            const day = date.getDate();
            const suffix = this.getOrdinalSuffix(day);
            el.textContent = 'Expected to ship ' + months[date.getMonth()] + ' ' + day + suffix;
            el.hidden = false;
          } else {
            el.hidden = true;
          }
        });
      }

      getOrdinalSuffix(day) {
        if (day >= 11 && day <= 13) return 'th';
        switch (day % 10) {
          case 1: return 'st';
          case 2: return 'nd';
          case 3: return 'rd';
          default: return 'th';
        }
      }

      /* ------------------------------------------------------------------ */
      /*  Options & pricing                                                 */
      /* ------------------------------------------------------------------ */

      allOptionsSelected() {
        return this.products.every((p) => {
          const vs = p.element.querySelector('variant-selects');
          if (!vs) return true;

          const fieldsets = vs.querySelectorAll('fieldset');
          if (fieldsets.length === 0) return true;

          return Array.from(fieldsets).every((fs) =>
            fs.querySelector('input:checked')
          );
        });
      }

      /**
       * Check if a product's selection is being fulfilled by a pre-order sibling.
       */
      isPreorder(product) {
        if (!product.selectedVariant || !product.cartVariant) return false;
        // The main variant is unavailable but we found a sibling variant
        return !product.selectedVariant.available && product.cartVariant.available;
      }

      updatePrice() {
        let total = 0;
        let compareTotal = 0;
        let hasCompare = false;

        this.products.forEach((p) => {
          const v = p.cartVariant || p.selectedVariant;
          if (v) {
            total += v.price;
            if (v.compare_at_price && v.compare_at_price > v.price) {
              compareTotal += v.compare_at_price;
              hasCompare = true;
            } else {
              compareTotal += v.price;
            }
          }
        });

        if (this.priceText) {
          this.priceText.textContent = this.formatMoney(total);
        }

        // Update both price displays (CSS toggles visibility via price--on-sale)
        const priceWrap = document.getElementById('price-' + this.sectionId);
        if (priceWrap) {
          const priceDiv = priceWrap.querySelector('.price');
          if (priceDiv) {
            priceDiv.classList.toggle('price--on-sale', hasCompare);
          }

          // Update all [data-bundle-current-price] elements (one in regular, one in sale)
          priceWrap.querySelectorAll('[data-bundle-current-price]').forEach(
            (el) => (el.textContent = this.formatMoney(total))
          );
          const comparePriceEl = priceWrap.querySelector('[data-bundle-compare-at-price]');
          if (comparePriceEl) {
            comparePriceEl.textContent = this.formatMoney(compareTotal);
          }

          // Save badge
          const saveBadge = priceWrap.querySelector('[data-bundle-save-badge]');
          if (saveBadge) {
            if (hasCompare && compareTotal > total) {
              const pct = Math.round(((compareTotal - total) / compareTotal) * 100);
              saveBadge.textContent = 'Save ' + pct + '%';
              saveBadge.hidden = false;
            } else {
              saveBadge.hidden = true;
            }
          }
        }
      }

      updateButtonState() {
        const allSelected = this.allOptionsSelected();
        const allResolved = this.products.every((p) => p.cartVariant);

        // "Available" means either the main variant is in stock OR a sibling pre-order is available
        const allAvailable = this.products.every(
          (p) => p.cartVariant && p.cartVariant.available
        );

        if (!allSelected) {
          this.submitButton.disabled = true;
          if (this.submitText) {
            this.submitText.innerHTML = 'Select your sizes';
          }
          if (this.invMsg) this.invMsg.hidden = true;
          if (this.splitNote) this.splitNote.hidden = true;
          return;
        }

        if (!allResolved || !allAvailable) {
          this.submitButton.disabled = true;
          const soldOutNames = this.products
            .filter((p) => !p.cartVariant || !p.cartVariant.available)
            .map((p) => {
              const title = p.element.querySelector(
                '.bundle-product-group__title'
              );
              return title ? title.textContent.trim() : 'item';
            });

          if (this.submitText) {
            this.submitText.innerHTML =
              'Sold Out — ' + soldOutNames.join(', ');
          }
          if (this.invMsg) this.invMsg.hidden = true;
          if (this.splitNote) this.splitNote.hidden = true;
          return;
        }

        // All good — enable
        this.submitButton.disabled = false;

        let total = 0;
        this.products.forEach((p) => (total += p.cartVariant.price));

        if (this.submitText) {
          this.submitText.innerHTML =
            'Add to Bag - <span data-bundle-price-text>' +
            this.formatMoney(total) +
            '</span>';
          this.priceText = this.querySelector('[data-bundle-price-text]');
        }

        // Check if any product is using a pre-order sibling
        const hasPreorder = this.products.some((p) => this.isPreorder(p));
        const hasInStock = this.products.some(
          (p) => p.selectedVariant && p.selectedVariant.available
        );
        const isSplitShipment = hasPreorder && hasInStock;

        if (this.invMsg) {
          if (hasPreorder && !hasInStock) {
            this.invMsg.textContent = 'Pre-Order: Ships When Available';
          } else {
            this.invMsg.textContent = 'In Stock: Ready to Ship';
          }
          this.invMsg.hidden = false;
        }

        if (this.splitNote) {
          this.splitNote.hidden = !isSplitShipment;
        }
      }

      /* ------------------------------------------------------------------ */
      /*  Cart submission                                                   */
      /* ------------------------------------------------------------------ */

      async handleSubmit() {
        if (this.submitButton.disabled) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true')
          return;

        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        // Use cartVariant (which may be a sibling pre-order variant)
        const items = this.products.map((p) => ({
          id: p.cartVariant.id,
          quantity: 1,
        }));

        const cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        try {
          const config = fetchConfig('javascript');
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify({
            items,
            sections: cart
              ? cart.getSectionsToRender().map((s) => s.id)
              : [],
            sections_url: window.location.pathname,
          });

          const response = await fetch(routes.cart_add_url, config);
          const data = await response.json();

          if (data.status) {
            console.error('Bundle cart add error:', data.description);
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'bundle-product-form',
            cartData: data,
          });

          if (typeof updateCartCount === 'function') {
            updateCartCount();
          }

          if (cart && cart.renderContents) {
            cart.renderContents(data);
          } else if (cart && cart.open) {
            cart.open();
          }
        } catch (e) {
          console.error('Bundle add to cart failed:', e);
        } finally {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        }
      }

      formatMoney(cents) {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          return Shopify.formatMoney(
            cents,
            window.shopifyMoneyFormat || '${{amount}}'
          );
        }
        const amount = cents / 100;
        if (amount % 1 === 0) return '$' + amount;
        return '$' + amount.toFixed(2);
      }
    }
  );
}
